import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  X,
  User,
  Mail,
  Phone,
  MapPin,
  Calendar,
  ShoppingBag,
  DollarSign,
  TrendingUp,
  Copy,
  Home,
  Star,
  Package,
  CheckCircle,
  Clock,
  AlertCircle,
  CreditCard,
  Bell,
  BellOff,
  Sparkles,
  Edit,
  Crown
} from 'lucide-react';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Separator } from './ui/separator';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { formatCurrency, formatDate, formatDateTime, getInitials } from '../lib/utils';
import { Customer, Order } from '../types';
import { toast } from 'sonner';
import { EditCustomerModal } from './EditCustomerModal';
import { OrderDetailsModal } from './OrderDetailsModal';

interface CustomerProfileModalProps {
  customer: Customer | null;
  isOpen: boolean;
  onClose: () => void;
  onCustomerUpdate?: (updatedCustomer: Customer) => void;
  onViewOrders?: (customer: Customer) => void;
}

export function CustomerProfileModal({ customer, isOpen, onClose, onCustomerUpdate, onViewOrders }: CustomerProfileModalProps) {
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState<Order | null>(null);
  const [showOrderDetailsModal, setShowOrderDetailsModal] = useState(false);
  
  if (!customer) return null;

  // Helper functions
  const getCustomerStatus = () => {
    const hasRecentOrder = customer.lastOrderDate && 
      new Date(customer.lastOrderDate) > new Date(Date.now() - 90 * 24 * 60 * 60 * 1000);
    return hasRecentOrder ? 'active' : 'inactive';
  };

  const getStatusConfig = (status: string) => {
    switch (status) {
      case 'active':
        return { color: 'bg-green-100 text-green-800', label: 'Active' };
      case 'inactive':
        return { color: 'bg-gray-100 text-gray-800', label: 'Inactive' };
      default:
        return { color: 'bg-gray-100 text-gray-800', label: 'Unknown' };
    }
  };

  const getPrimaryAddress = () => {
    return customer.addresses?.find(addr => addr.isDefault) || customer.addresses?.[0];
  };

  const copyCustomerId = () => {
    navigator.clipboard.writeText(customer.id);
    toast.success('Customer ID copied to clipboard');
  };

  const copyEmail = () => {
    if (customer.email) {
      navigator.clipboard.writeText(customer.email);
      toast.success('Email copied to clipboard');
    }
  };

  const copyPhone = () => {
    const primaryAddress = getPrimaryAddress();
    if (primaryAddress?.phoneNumber) {
      navigator.clipboard.writeText(primaryAddress.phoneNumber);
      toast.success('Phone number copied to clipboard');
    }
  };

  const handleEditProfile = () => {
    setShowEditModal(true);
  };

  const handleEditModalClose = () => {
    setShowEditModal(false);
  };

  const handleCustomerSave = async (updatedCustomer: Customer) => {
    if (onCustomerUpdate) {
      onCustomerUpdate(updatedCustomer);
    }
    setShowEditModal(false);
  };

  const handleViewOrders = () => {
    if (onViewOrders) {
      onViewOrders(customer);
    }
  };

  const handleViewOrderDetails = (order: Order) => {
    setSelectedOrder(order);
    setShowOrderDetailsModal(true);
  };

  const statusConfig = getStatusConfig(getCustomerStatus());
  const primaryAddress = getPrimaryAddress();
  const rushdMembership = customer.rushdMembership;
  const isRushdMember = !!rushdMembership?.isMember;

  const getRushdStatusConfig = () => {
    if (!rushdMembership) {
      return { label: 'Not a member', badgeClass: 'bg-gray-100 text-gray-700 border-gray-200' };
    }

    if (rushdMembership.isActive) {
      return { label: 'Active', badgeClass: 'bg-emerald-100 text-emerald-800 border-emerald-200' };
    }

    switch (rushdMembership.status) {
      case 'past_due':
        return { label: 'Past Due', badgeClass: 'bg-red-100 text-red-800 border-red-200' };
      case 'canceled':
        return { label: 'Canceled', badgeClass: 'bg-gray-100 text-gray-800 border-gray-200' };
      case 'trialing':
        return { label: 'Trialing', badgeClass: 'bg-blue-100 text-blue-800 border-blue-200' };
      case 'incomplete':
        return { label: 'Incomplete', badgeClass: 'bg-amber-100 text-amber-800 border-amber-200' };
      default:
        return { label: 'Inactive', badgeClass: 'bg-amber-100 text-amber-800 border-amber-200' };
    }
  };

  const formatMembershipDate = (date?: Date) => {
    if (!date) {
      return 'Not available';
    }
    return formatDate(date);
  };

  const rushdStatusConfig = getRushdStatusConfig();

  return (
    <>
      <AnimatePresence>
        {isOpen && (
          <div className="fixed inset-0 bg-black/60 backdrop-blur-md z-[10001] flex items-center justify-center p-4" style={{ margin: 0, padding: '1rem', top: 0, left: 0, right: 0, bottom: 0 }}>
            <motion.div
              initial={{ opacity: 0, scale: 0.9, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.9, y: 20 }}
              transition={{ 
                type: "spring",
                duration: 0.5,
                bounce: 0.3 
              }}
              className="bg-white rounded-2xl shadow-2xl w-full max-w-6xl max-h-[90vh] flex flex-col overflow-hidden"
            >
              {/* Enhanced Header */}
              <div className="relative bg-gradient-to-r from-blue-600 to-indigo-600 p-6 flex-shrink-0">
                <div className="absolute inset-0 bg-gradient-to-r from-blue-600/20 to-transparent"></div>
                <div className="relative flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <motion.div 
                      initial={{ scale: 0 }}
                      animate={{ scale: 1 }}
                      transition={{ delay: 0.2, type: "spring", bounce: 0.6 }}
                      className="h-16 w-16 bg-white/20 backdrop-blur-sm rounded-xl flex items-center justify-center border border-white/30"
                    >
                      <span className="text-white font-bold text-xl">{getInitials(customer.name || 'Unknown')}</span>
                    </motion.div>
                    <div>
                      <motion.h2 
                        initial={{ opacity: 0, x: -20 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ delay: 0.1 }}
                        className="text-2xl font-bold text-white"
                      >
                        {customer.name || 'Unknown Customer'}
                      </motion.h2>
                      <motion.div 
                        initial={{ opacity: 0, x: -20 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ delay: 0.2 }}
                        className="flex items-center gap-4 text-white/80 text-sm mt-1"
                      >
                        <div className="flex items-center gap-1">
                          <span>ID: {customer.id}</span>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={copyCustomerId}
                            className="p-1 h-auto text-white/80 hover:text-white hover:bg-white/10"
                          >
                            <Copy className="w-3 h-3" />
                          </Button>
                        </div>
                        <Badge className={`${statusConfig.color} border rounded-lg`}>
                          {statusConfig.label}
                        </Badge>
                      </motion.div>
                    </div>
                  </div>
                  <motion.div
                    whileHover={{ scale: 1.1 }}
                    whileTap={{ scale: 0.9 }}
                  >
                    <Button 
                      variant="ghost" 
                      size="sm" 
                      onClick={onClose}
                      className="text-white hover:bg-white/20 h-10 w-10 rounded-full"
                    >
                      <X className="h-5 w-5" />
                    </Button>
                  </motion.div>
                </div>
              </div>

              {/* Enhanced Content */}
              <div className="flex-1 overflow-y-auto bg-gradient-to-b from-gray-50/50 to-white">
                <div className="p-6">
                  <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                    {/* Left Column - Personal Information */}
                    <div className="lg:col-span-1 space-y-6">
                      {/* Contact Information */}
                      <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.3 }}
                      >
                        <Card className="border border-gray-200 shadow-sm rounded-xl">
                          <CardHeader className="pb-3">
                            <CardTitle className="flex items-center gap-2 text-base">
                              <User className="w-4 h-4 text-blue-600" />
                              Contact Information
                            </CardTitle>
                          </CardHeader>
                          <CardContent className="space-y-4">
                            {/* Email */}
                            <div>
                              <div className="flex items-center justify-between mb-1">
                                <label className="text-sm font-medium text-gray-700">Email</label>
                                {customer.email && (
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={copyEmail}
                                    className="p-1 h-auto text-gray-500 hover:text-gray-700"
                                  >
                                    <Copy className="w-3 h-3" />
                                  </Button>
                                )}
                              </div>
                              <div className="flex items-center gap-2">
                                <Mail className="w-4 h-4 text-gray-400" />
                                <span className="text-sm text-gray-600">
                                  {customer.email || 'Not provided'}
                                </span>
                              </div>
                            </div>

                            {/* Phone */}
                            <div>
                              <div className="flex items-center justify-between mb-1">
                                <label className="text-sm font-medium text-gray-700">Phone</label>
                                {primaryAddress?.phoneNumber && (
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={copyPhone}
                                    className="p-1 h-auto text-gray-500 hover:text-gray-700"
                                  >
                                    <Copy className="w-3 h-3" />
                                  </Button>
                                )}
                              </div>
                              <div className="flex items-center gap-2">
                                <Phone className="w-4 h-4 text-gray-400" />
                                <span className="text-sm text-gray-600">
                                  {primaryAddress?.phoneNumber || 'Not provided'}
                                </span>
                              </div>
                            </div>

                            {/* Join Date */}
                            <div>
                              <label className="text-sm font-medium text-gray-700 mb-1 block">Member Since</label>
                              <div className="flex items-center gap-2">
                                <Calendar className="w-4 h-4 text-gray-400" />
                                <span className="text-sm text-gray-600">
                                  {formatDate(customer.createdAt)}
                                </span>
                              </div>
                            </div>
                          </CardContent>
                        </Card>
                      </motion.div>

                      {isRushdMember && rushdMembership && (
                        <motion.div
                          initial={{ opacity: 0, y: 20 }}
                          animate={{ opacity: 1, y: 0 }}
                          transition={{ delay: 0.35 }}
                        >
                          <Card className="border border-amber-200 shadow-sm rounded-xl bg-gradient-to-br from-amber-50 to-white">
                            <CardHeader className="pb-3">
                              <CardTitle className="flex items-center gap-2 text-base text-amber-900">
                                <span className="p-2 bg-white/80 rounded-full text-amber-600">
                                  <Crown className="w-4 h-4" />
                                </span>
                                JamPack'd Rush'd
                              </CardTitle>
                            </CardHeader>
                            <CardContent className="space-y-4">
                              <div className="flex items-center justify-between">
                                <div>
                                  <p className="text-sm text-amber-900 font-semibold">
                                    {rushdMembership.plan ? `${rushdMembership.plan.charAt(0).toUpperCase()}${rushdMembership.plan.slice(1)} plan` : 'Member'}
                                  </p>
                                  <p className="text-xs text-amber-700 mt-0.5">
                                    Priority packing and member pricing enabled
                                  </p>
                                </div>
                                <Badge className={`${rushdStatusConfig.badgeClass} border rounded-lg`}>
                                  {rushdStatusConfig.label}
                                </Badge>
                              </div>

                              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm text-gray-600">
                                <div>
                                  <p className="text-xs uppercase tracking-wide text-gray-500">Current Period Ends</p>
                                  <p className="font-semibold text-gray-900">
                                    {formatMembershipDate(rushdMembership.currentPeriodEnd)}
                                  </p>
                                </div>
                              </div>

                              {typeof rushdMembership.usageLimit === 'number' && (
                                <div className="space-y-2">
                                  <div className="flex items-center justify-between text-xs text-gray-600">
                                    <span>Monthly usage</span>
                                    <span className="font-medium text-gray-900">
                                      {rushdMembership.usageThisPeriod ?? 0}/{rushdMembership.usageLimit}
                                    </span>
                                  </div>
                                  <div className="h-2 bg-amber-100 rounded-full overflow-hidden">
                                    <div
                                      className="h-full bg-amber-500 transition-all"
                                      style={{
                                        width: `${Math.min(
                                          100,
                                          ((rushdMembership.usageThisPeriod ?? 0) / rushdMembership.usageLimit) * 100
                                        )}%`
                                      }}
                                    />
                                  </div>
                                </div>
                              )}

                              {rushdMembership.cancelAtPeriodEnd && (
                                <div className="flex items-center gap-2 text-xs text-amber-700 bg-amber-50 border border-amber-100 rounded-lg px-3 py-2">
                                  <AlertCircle className="w-4 h-4" />
                                  Scheduled to cancel at the end of this billing period
                                </div>
                              )}
                            </CardContent>
                          </Card>
                        </motion.div>
                      )}

                      {/* Notification Preferences */}
                      <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.4 }}
                      >
                        <Card className="border border-gray-200 shadow-sm rounded-xl">
                          <CardHeader className="pb-3">
                            <CardTitle className="flex items-center gap-2 text-base">
                              <Bell className="w-4 h-4 text-blue-600" />
                              Notification Preferences
                            </CardTitle>
                          </CardHeader>
                          <CardContent className="space-y-3">
                            <div className="flex items-center justify-between">
                              <span className="text-sm text-gray-600">Order updates</span>
                              {customer.notificationPreferences?.orderUpdates ? (
                                <Badge className="bg-green-100 text-green-800 text-xs">Enabled</Badge>
                              ) : (
                                <Badge className="bg-gray-100 text-gray-800 text-xs">Disabled</Badge>
                              )}
                            </div>
                            <div className="flex items-center justify-between">
                              <span className="text-sm text-gray-600">Promotions</span>
                              {customer.notificationPreferences?.promotions ? (
                                <Badge className="bg-green-100 text-green-800 text-xs">Enabled</Badge>
                              ) : (
                                <Badge className="bg-gray-100 text-gray-800 text-xs">Disabled</Badge>
                              )}
                            </div>
                            <div className="flex items-center justify-between">
                              <span className="text-sm text-gray-600">Newsletters</span>
                              {customer.notificationPreferences?.newsletters ? (
                                <Badge className="bg-green-100 text-green-800 text-xs">Enabled</Badge>
                              ) : (
                                <Badge className="bg-gray-100 text-gray-800 text-xs">Disabled</Badge>
                              )}
                            </div>
                          </CardContent>
                        </Card>
                      </motion.div>
                    </div>

                    {/* Right Column - Order History & Addresses */}
                    <div className="lg:col-span-2 space-y-6">
                      {/* Quick Actions */}
                      <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.5 }}
                        className="flex flex-wrap gap-3"
                      >
                        <motion.div whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}>
                          <Button
                            onClick={handleEditProfile}
                            className="bg-blue-600 hover:bg-blue-700 text-white px-6 h-11 rounded-xl font-semibold shadow-lg shadow-blue-600/25"
                          >
                            <Edit className="w-4 h-4 mr-2" />
                            Edit Profile
                          </Button>
                        </motion.div>
                        <motion.div whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}>
                          <Button
                            onClick={handleViewOrders}
                            variant="outline"
                            className="px-6 h-11 rounded-xl font-medium border-blue-200 text-blue-700 hover:bg-blue-50"
                          >
                            <ShoppingBag className="w-4 h-4 mr-2" />
                            View Orders
                          </Button>
                        </motion.div>
                      </motion.div>

                      {/* Address Information */}
                      <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.6 }}
                      >
                        <Card className="border border-gray-200 shadow-sm rounded-xl">
                          <CardHeader className="pb-3">
                            <CardTitle className="flex items-center gap-2 text-base">
                              <MapPin className="w-4 h-4 text-blue-600" />
                              Addresses ({customer.addresses?.length || 0})
                            </CardTitle>
                          </CardHeader>
                          <CardContent>
                            {customer.addresses && customer.addresses.length > 0 ? (
                              <div className="space-y-3">
                                {customer.addresses.map((address, index) => (
                                  <motion.div 
                                    key={address.id} 
                                    initial={{ opacity: 0, x: -20 }}
                                    animate={{ opacity: 1, x: 0 }}
                                    transition={{ delay: 0.7 + index * 0.1 }}
                                    className="border rounded-xl p-4 hover:shadow-md transition-shadow bg-gray-50/50"
                                  >
                                    <div className="flex items-start justify-between mb-2">
                                      <div className="flex items-center gap-2">
                                        <Home className="w-4 h-4 text-gray-400" />
                                        <span className="font-medium text-sm text-gray-900">
                                          {address.label || `Address ${index + 1}`}
                                        </span>
                                      </div>
                                      {address.isDefault && (
                                        <Badge className="bg-blue-100 text-blue-800 text-xs">Default</Badge>
                                      )}
                                    </div>
                                    <div className="text-sm text-gray-600 space-y-1 ml-6">
                                      <div className="font-medium">{address.fullName}</div>
                                      <div>{address.line1}</div>
                                      {address.line2 && <div>{address.line2}</div>}
                                      <div>{address.city}, {address.state} {address.zipCode}</div>
                                      <div>{address.country}</div>
                                      {address.phoneNumber && (
                                        <div className="flex items-center gap-1 mt-2">
                                          <Phone className="w-3 h-3 text-gray-400" />
                                          <span>{address.phoneNumber}</span>
                                        </div>
                                      )}
                                    </div>
                                  </motion.div>
                                ))}
                              </div>
                            ) : (
                              <div className="text-center py-8 text-gray-500">
                                <MapPin className="w-8 h-8 mx-auto mb-2 text-gray-400" />
                                <p className="text-sm">No addresses on file</p>
                              </div>
                            )}
                          </CardContent>
                        </Card>
                      </motion.div>
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      {/* Edit Customer Modal */}
      <EditCustomerModal
        customer={customer}
        isOpen={showEditModal}
        onClose={handleEditModalClose}
        onSave={handleCustomerSave}
      />

      {/* Order Details Modal */}
      <OrderDetailsModal
        order={selectedOrder}
        isOpen={showOrderDetailsModal}
        onClose={() => setShowOrderDetailsModal(false)}
      />
    </>
  );
} 
